import React, { useEffect, useState } from 'react';
import { BusinessSubscription } from 'subscriptions/types/Subscription';
import { formatDate } from '../../utils/formatDate';
import { useTranslations } from '../../localization/useTranslations';
import { getBusinessSubChildren } from '../services/SubscriptionService';
import { BusinessSubChild } from '../types/Subscription';
import Button from '../../components/buttons/SimpleButton/Button';

const BusinessSubscriptionRow = ({ subscription, exportToCsv }: BusinessSubscription) => {
  const translations = useTranslations();

  // State variables
  const [activeTab, setActiveTab] = useState('waiting');
  const [showAllChildrenWaiting, setShowAllChildrenWaiting] = useState(false);
  const [showAllChildrenActivated, setShowAllChildrenActivated] = useState(false);
  const maxChildrenToShow = 4;
  const [subscriptionCodes, setSubscriptionCodes] = useState<BusinessSubChild[]>([]);
  const hasSubscriptionCodes =
    subscriptionCodes.length > 0 &&
    subscriptionCodes.some((child) =>
      activeTab === 'waiting' ? child.status === 'created' : child.status === 'accepted'
    );

  const subActivationSlug = window?.activateSubPageSlug || 'activate-sub';

  /**
   * Fetches subscription codes and sets them using the business child response.
   *
   * @param {} -
   * @return {Promise<void>}
   */
  useEffect(() => {
    const fetchSubscriptionCodes = async () => {
      try {
        const businessChildResponse = await getBusinessSubChildren(subscription.id);
        setSubscriptionCodes(businessChildResponse.codes);
      } catch (error) {
        console.error('Error fetching subscription codes', error);
      }
    };

    fetchSubscriptionCodes();
  }, [subscription.id]);

  // Determine the active tab based on the presence of waiting children
  useEffect(() => {
    const hasWaitingChildren = subscriptionCodes.some((child) => child.status === 'created');

    setActiveTab(hasWaitingChildren ? 'waiting' : 'activated');
  }, [subscriptionCodes]);

  // Calculate the count of remaining waiting and activated children
  const remainingChildrenCountWaiting =
    subscriptionCodes.filter((child) => child.status === 'created').length - maxChildrenToShow;
  const remainingChildrenCountActivated =
    subscriptionCodes.filter((child) => child.status === 'accepted').length - maxChildrenToShow;

  // Handle the click event to show all children based on the active tab
  const handleShowAllClick = () => {
    if (activeTab === 'waiting') {
      setShowAllChildrenWaiting(true);
    } else if (activeTab === 'activated') {
      setShowAllChildrenActivated(true);
    }
  };

  // Determine the visibility of the "Show All" button for waiting children
  const isShowAllButtonVisible =
    !showAllChildrenWaiting &&
    activeTab === 'waiting' &&
    subscriptionCodes.length > maxChildrenToShow &&
    remainingChildrenCountWaiting > 0;

  // Determine the visibility of the "Show All" button for activated children
  const isShowAllActivatedButtonVisible =
    !showAllChildrenActivated &&
    activeTab === 'activated' &&
    subscriptionCodes.length > maxChildrenToShow &&
    remainingChildrenCountActivated > 0;

  // Handle the copy to clipboard functionality for activation codes
  const handleCopyToClipboard = (code: string) => {
    const urlToCopy = window.location.origin + '/' + subActivationSlug + '/?code=' + code;

    const textarea = document.createElement('textarea');
    textarea.value = urlToCopy;
    document.body.appendChild(textarea);

    textarea.select();
    document.execCommand('copy');

    document.body.removeChild(textarea);

    // Show an alert after copying to the clipboard
    alert(translations?.myAccountSubscription?.copiedActivationCode || 'Copied activation code to clipboard');
  };

  const filteredSubscriptionCodes = subscriptionCodes
    .filter((child) => (activeTab === 'waiting' ? child.status === 'created' : child.status === 'accepted'))
    .slice(
      0,
      activeTab === 'waiting'
        ? showAllChildrenWaiting
          ? undefined
          : maxChildrenToShow
        : showAllChildrenActivated
          ? undefined
          : maxChildrenToShow
    );

  return (
    <div className="my-subscriptions__business">
      <div className="my-subscriptions__section-header">
        <h4 className="my-subscriptions__section-title my-subscriptions__section-title--business archivo">
          {subscription.name}
        </h4>

        <span className="my-subscriptions__label my-subscriptions__label--date basic basic-14">
          {'(' + formatDate(subscription.start_at) + ' - ' + formatDate(subscription.end_at) + ')'}
        </span>

        <div className="my-subscriptions__tab-buttons-wrapper">
          <div
            onClick={() => setActiveTab('waiting')}
            className={`my-subscriptions__tab-button my-subscriptions__tab-button--waiting ${
              activeTab === 'waiting' ? 'active' : ''
            } button-text`}
          >
            {translations?.myAccountSubscription?.waitingForActivation || 'Waiting for activation'}
          </div>

          <div
            onClick={() => setActiveTab('activated')}
            className={`my-subscriptions__tab-button my-subscriptions__tab-button--activated ${
              activeTab === 'activated' ? 'active' : ''
            } button-text`}
          >
            {translations?.myAccountSubscription?.activated || 'Activated'}
          </div>
        </div>
      </div>

      {hasSubscriptionCodes ? (
        <table className="my-subscriptions__table my-subscriptions__table--my-subscriptions__section my-subscriptions__section--business">
          <thead className="my-subscriptions__table-head">
            <tr className="my-subscriptions__table-header-row">
              <th className="my-subscriptions__table-header basic basic-14">
                {translations?.myAccountSubscription?.subscriptionType || 'Subscription type'}
              </th>

              <th className="my-subscriptions__table-header basic basic-14">
                {translations?.orderLabels?.status || 'Status'}
              </th>
            </tr>
          </thead>

          <tbody className="my-subscriptions__table-body">
            {filteredSubscriptionCodes.map((child, childIndex) => (
              <tr className="my-subscriptions__table-row" key={childIndex}>
                <td className="my-subscriptions__table-data-wrapper">
                  <span className="my-subscriptions__data-label-mobile basic basic-16">
                    {translations?.myAccountSubscription?.subscriptionType || 'Subscription type'}
                  </span>

                  <div className="my-subscriptions__table-data-inner-wrapper basic basic-16">
                    <span className="my-subscriptions__table-data my-subscriptions__table-data--type basic basic-16">
                      {subscription.name}
                    </span>

                    {child.status === 'accepted' && (
                      <span className="my-subscriptions__data-label basic basic-14">{child.accepted_by}</span>
                    )}
                  </div>
                </td>

                <td className="my-subscriptions__table-data-wrapper">
                  <span className="my-subscriptions__data-label-mobile basic basic-16">
                    {translations?.orderLabels?.status || 'Status'}
                  </span>

                  <span className="my-subscriptions__table-data my-subscriptions__table-data--status basic basic-16">
                    {child.status === 'accepted' ? (
                      <>
                        {translations?.myAccountSubscription?.activated || 'activated'} {formatDate(child.accepted_at)}
                      </>
                    ) : (
                      translations?.myAccountSubscription.waitingForActivation || 'waiting for activation'
                    )}
                  </span>
                </td>

                <td className="my-subscriptions__table-data-wrapper">
                  <div className="my-subscriptions__table-data my-subscriptions__table-data--button">
                    {child.status === 'created' && (
                      <Button
                        onClick={() => handleCopyToClipboard(child.code)}
                        className="my-subscriptions__button my-subscriptions__button--copy"
                        type="secondary"
                        size="medium"
                        label={translations?.myAccountSubscription?.copyUrlButton || 'Copy URL'}
                      ></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // Render the empty state when there are no subscription codes
        <>
          {activeTab === 'waiting' && subscriptionCodes.filter((child) => child.status === 'created').length === 0 && (
            <div id="my-subscriptions-empty-state" className="empty-state">
              <p className="empty-message">
                {translations?.emptyStates?.noWaitingSubscriptions || 'No items to display for Waiting for Activation'}
              </p>
            </div>
          )}

          {activeTab === 'activated' &&
            subscriptionCodes.filter((child) => child.status === 'accepted').length === 0 && (
              <div id="my-subscriptions-empty-state" className="empty-state">
                <p className="empty-message">
                  {translations?.emptyStates?.noActivatedBusinessSubscriptions || 'No items to display for Activated'}
                </p>
              </div>
            )}
        </>
      )}

      <div className="my-subscriptions__button-wrapper my-subscriptions__button-wrapper--business">
        {isShowAllButtonVisible && (
          <Button
            onClick={handleShowAllClick}
            className="my-subscriptions__button my-subscriptions__button--expand"
            type="primary"
            size="medium"
            label={`${translations?.myAccountSubscription?.showAll || 'Show all'} (${remainingChildrenCountWaiting})`}
          ></Button>
        )}

        {isShowAllActivatedButtonVisible && (
          <Button
            onClick={handleShowAllClick}
            className="my-subscriptions__button my-subscriptions__button--expand"
            type="primary"
            size="medium"
            label={`${translations?.myAccountSubscription?.showAll || 'Show all'} (${remainingChildrenCountActivated})`}
          ></Button>
        )}

        {activeTab === 'waiting' && (
          <Button
            onClick={() => exportToCsv(subscription.id)}
            className="my-subscriptions__button my-subscriptions__button--export"
            type="secondary"
            size="medium"
            label={translations?.myAccountSubscription?.exportUrlsToCvs || 'Export URLs to CSV'}
          ></Button>
        )}
      </div>
    </div>
  );
};

export default BusinessSubscriptionRow;
